import { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { ShareApiCall } from 'reactApp/api/sharing/ShareApiCall';
import { IInvite } from 'reactApp/api/sharing/SharedInfoApiCall';
import { UnshareApiCall } from 'reactApp/api/sharing/UnshareApiCall';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { ON_PREMISE_WHITELIST_DOMAINS } from 'reactApp/constants/whiteListDomains';
import { contactsSlice } from 'reactApp/modules/contacts/contacts.module';
import { shareFolderSuccess, unshareFolderSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { getInfo } from 'reactApp/modules/modifying/sagas/mount.saga';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { validateEmail, validateEmailWhiteListDomain } from 'reactApp/utils/helpers';
import { all, put, select, takeEvery } from 'redux-saga/effects';

import {
    accessControlListLoadStart,
    deleteAllUsers,
    deleteUser,
    setAccessControlListError,
    setAccessRight,
    setInvitesSuccess,
} from './folderAccessControlList.actions';
import { getSharingError } from './folderAccessControlList.helpers';
import { IAccessControlListLoadStart, IDeleteAllUser, IDeleteUser, ISetAccessRight } from './folderAccessControlList.types';

const unshareApiCall = ({ id, email }) => new UnshareApiCall().makeRequest({ home: id, invite: { email } });
const shareApiCall = ({ id, email, access }) => new ShareApiCall().makeRequest({ home: id, invite: { email, access } });

function* handleUnshareRequest({ id, email }) {
    try {
        const { data } = yield unshareApiCall({ id, email });

        return { info: data.body };
    } catch (error) {
        logger.error(error);
    }
}

function* loadStart(action: PayloadAction<IAccessControlListLoadStart>) {
    const { id } = action.payload;
    const item = yield select((state) => getItemById(state, id));
    const { info, error } = yield getInfo(item);

    if (error) {
        yield put(setAccessControlListError({ error: 'Не удалось получить список' }));
        return;
    }

    const userEmail = yield select(UserSelectors.getEmail);
    const owner = info.owner || { email: userEmail };

    yield put(setInvitesSuccess({ invited: info.invited, owner }));
}

function* setAccessRightRequest(action: PayloadAction<ISetAccessRight>) {
    const { accessRight, email, folderId } = action.payload;
    const currentEmail = email.trim();

    if (!validateEmail(email)) {
        yield put(
            setAccessControlListError({
                error: 'Вы ввели неправильные символы. Убедитесь в правильном написании электронного адреса и попробуйте снова',
            })
        );
        return;
    }

    // валидируем домены только в коробке, и только когда задан нужный конфиг
    if (IS_ONPREMISE && ON_PREMISE_WHITELIST_DOMAINS && !validateEmailWhiteListDomain(email, ON_PREMISE_WHITELIST_DOMAINS)) {
        yield put(setAccessControlListError({ error: 'Вы можете открыть доступ к папке только другому пользователю домена' }));
        return;
    }

    yield put(contactsSlice.actions.addRequest([{ emails: [email] }]));

    try {
        const { data } = yield shareApiCall({ id: folderId, email: currentEmail, access: accessRight });
        const info = data.body;

        yield put(setInvitesSuccess({ invited: info.invited }));
        yield put(shareFolderSuccess({ id: folderId }));
    } catch (error) {
        logger.error(error);

        const isVerifiedAccount = yield select(UserSelectors.isVerifiedAccount);
        yield put(setAccessControlListError({ error: getSharingError(error, isVerifiedAccount) }));
    }
}

function* handleDeleteUser(action: PayloadAction<IDeleteUser>) {
    const { item, email } = action.payload;
    const { info } = yield getInfo(item);
    const id = info.home;

    if (!info?.invited?.length) {
        yield put(setInvitesSuccess({ invited: [] }));
        yield put(unshareFolderSuccess({ id }));
        return;
    }

    const { info: newInfo } = yield handleUnshareRequest({ id: item.id, email });

    yield put(setInvitesSuccess({ invited: newInfo.invited || [] }));

    if (!newInfo.invited?.length) {
        yield put(unshareFolderSuccess({ id }));
    }
}

function* handleDeleteAllUsers(action: PayloadAction<IDeleteAllUser>) {
    const { item } = action.payload;
    const { info } = yield getInfo(item);
    const id = info.home;

    if (info?.invited?.length) {
        yield all(info.invited.map((invite: IInvite) => handleUnshareRequest({ id: item.id, email: invite.email })));
    }

    yield put(setInvitesSuccess({ invited: [] }));
    yield put(unshareFolderSuccess({ id }));
}

export function* watchAccessControlList() {
    yield takeEvery(accessControlListLoadStart.toString(), loadStart);
    yield takeEvery(setAccessRight.toString(), setAccessRightRequest);
    yield takeEvery(deleteUser.toString(), handleDeleteUser);
    yield takeEvery(deleteAllUsers.toString(), handleDeleteAllUsers);
}
